/**
 * AppErrorBoundary — top-level React error boundary.
 *
 * Operator brief (Feb 19, 2026):
 *   "No white-screen death states. The platform shell must survive."
 *
 * Catches any render-time crash anywhere in the tree, reports it to
 * /api/debug/client-errors with a fresh ref ID, and renders a calm
 * slate recovery surface (NOT red, NO panic copy).
 *
 * Preview environments additionally show a collapsed stack trace
 * inside <details> for fast triage. Production never reveals stack
 * traces in the UI — admins read them via GET /api/debug/client-errors.
 */
import React from "react";
import {
  generateRefId,
  isPreviewEnv,
  reportClientError,
} from "../lib/clientErrorReporter";


class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      refId: null,
      message: null,
      stack: null,
    };
    this._reset = this._reset.bind(this);
    this._goHome = this._goHome.bind(this);
    this._hardReload = this._hardReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      refId: generateRefId(),
      message: (error && (error.message || String(error))) || "Render crash",
      stack: (error && error.stack) || null,
    };
  }

  componentDidCatch(error, info) {
    // Use the ref ID we generated in getDerivedStateFromError so the
    // server record matches what the UI is showing.
    reportClientError({
      refId: this.state.refId || undefined,
      category: "render_crash",
      component: info?.componentStack
        ? String(info.componentStack).split("\n").map((s) => s.trim()).filter(Boolean)[0] || null
        : null,
      error,
      extras: {
        componentStack: info?.componentStack
          ? String(info.componentStack).slice(0, 4000)
          : null,
      },
    });
  }

  _reset() {
    // Hard-reload the current route. A soft state reset is unsafe
    // here: if the underlying route deterministically throws on
    // mount (e.g. an unconfigured 3rd-party integration), clearing
    // the boundary state simply re-renders the same component and
    // re-traps the user. A real reload forces React + service-
    // worker caches to re-evaluate.
    try {
      window.location.reload();
    } catch (_) {
      try { window.location.href = window.location.pathname || "/"; }
      catch (_) { /* noop */ }
    }
  }

  _goHome() {
    // Hard-fallback navigation: assign + reload. `assign("/")` alone
    // can leave the user on a stale shell if a service worker has
    // cached the current page. `window.location.href = "/"` and a
    // best-effort follow-up reload guarantee the user escapes.
    try {
      window.location.href = "/";
    } catch (_) {
      try { window.location.assign("/"); } catch (_) { /* noop */ }
    }
    // Belt and braces — if we're already at "/" then `href` is a
    // no-op; force a reload so the recovery shell unmounts.
    try {
      setTimeout(() => {
        try { window.location.reload(); } catch (_) { /* noop */ }
      }, 50);
    } catch (_) { /* noop */ }
  }

  _hardReload() {
    // Operator-locked escape hatch (Feb 27, 2026). Always works,
    // independent of service-worker state. Wired as a third button
    // in the recovery surface so the user is never trapped.
    try {
      window.location.href = "/";
    } catch (_) { /* noop */ }
    try {
      setTimeout(() => {
        try { window.location.reload(); } catch (_) { /* noop */ }
      }, 50);
    } catch (_) { /* noop */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const showStack = isPreviewEnv() && this.state.stack;

    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-dvh flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4 py-12"
      >
        <div className="max-w-md w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Something interrupted the page
          </div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
            We caught the issue and saved a reference.
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            You haven&rsquo;t lost anything. Try the page again, or head back
            home — whichever feels easier.
          </p>

          <div
            data-testid="app-error-boundary-ref"
            className="inline-flex items-center gap-2 text-[11px] font-mono px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 mb-4"
          >
            <span className="opacity-70">Ref</span>
            <span className="font-semibold">{this.state.refId}</span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="app-error-boundary-retry"
              onClick={this._reset}
              className="inline-flex items-center justify-center text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 rounded-md px-3 py-2"
            >
              Try this page again
            </button>
            <button
              type="button"
              data-testid="app-error-boundary-home"
              onClick={this._goHome}
              className="inline-flex items-center justify-center text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md px-3 py-2"
            >
              Go home
            </button>
            <button
              type="button"
              data-testid="app-error-boundary-hard-reload"
              onClick={this._hardReload}
              className="inline-flex items-center justify-center text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md px-3 py-2"
            >
              Reload the app
            </button>
          </div>

          {/* Belt-and-braces hard escape — always-visible plain link
              that bypasses the React event system entirely. If the
              buttons are somehow non-functional (cached JS bundle,
              event-handler exception), this anchor still works. */}
          <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            Still stuck?{" "}
            <a
              data-testid="app-error-boundary-fallback-anchor"
              href="/"
              className="underline font-semibold"
            >
              Open the home page
            </a>
            .
          </p>

          {showStack ? (
            <details
              data-testid="app-error-boundary-stack"
              className="mt-4 text-[11px] text-slate-500 dark:text-slate-400"
            >
              <summary className="cursor-pointer select-none">
                Preview · diagnostic detail
              </summary>
              <pre className="mt-2 whitespace-pre-wrap break-all max-h-64 overflow-auto text-[10px] leading-tight">
                {this.state.message}
                {"\n\n"}
                {this.state.stack}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}


export default AppErrorBoundary;
